"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { ListPlus, Plus, Check, ListMusic, Loader2 } from 'lucide-react';
import { SlideshowImage } from '@/components/ui/slideshow-image';
import { useAuth } from '@/contexts/auth-context';
import {
  getUserPlaylists, createPlaylist, addTrackToPlaylist,
  type Playlist,
} from '@/services/playlists';
import { useToast } from '@/hooks/use-toast';

interface TrackInfo {
  trackId: string;
  title: string;
  artist: string;
  imageUrl?: string;
  audioUrl?: string;
  youtubeId?: string;
  duration?: number;
}

interface AddToPlaylistProps {
  /** Single-track mode — pass one track (for individual work/track cards) */
  track?: TrackInfo;
  /** Multi-track mode — pass all tracks for a release; adds them all to the playlist */
  tracks?: TrackInfo[];
  /** Label shown in the dialog when adding a full release */
  releaseTitle?: string;
  variant?: 'icon' | 'button' | 'menu-item';
  className?: string;
}

export function AddToPlaylist({ track, tracks: tracksBatch, releaseTitle, variant = 'icon', className = '' }: AddToPlaylistProps) {
  const { user, userProfile } = useAuth();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [playlists, setPlaylists] = useState<Playlist[]>([]);
  const [loading, setLoading] = useState(false);
  const [addedTo, setAddedTo] = useState<Set<string>>(new Set());
  // Artwork just added, shown in the playlist's thumbnail slideshow at once.
  const [justAdded, setJustAdded] = useState<Record<string, string[]>>({});
  const [showNewForm, setShowNewForm] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);

  // Resolve what we're actually adding — single track or batch of tracks from a release
  const isRelease = !!(tracksBatch && tracksBatch.length > 0);
  // For dialog description
  const displayTitle = isRelease ? (releaseTitle || 'this release') : (track?.title || '');
  // Playable tracks only (need audioUrl)
  const resolvedTracks: TrackInfo[] = isRelease
    ? tracksBatch!.filter(t => t.audioUrl && t.trackId)
    : track ? [track] : [];

  const loadPlaylists = useCallback(async () => {
    if (!user?.uid) return;
    setLoading(true);
    try {
      const pls = await getUserPlaylists(user.uid);
      setPlaylists(pls);
    } catch (err) {
      console.error('Error loading playlists:', err);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  useEffect(() => {
    if (open) {
      loadPlaylists();
      setAddedTo(new Set());
      setShowNewForm(false);
      setNewTitle('');
    }
  }, [open, loadPlaylists]);

  const handleAddToPlaylist = async (playlistId: string) => {
    if (addedTo.has(playlistId)) return;
    if (resolvedTracks.length === 0) {
      toast({ title: 'Nothing to add', description: 'No playable tracks found.', variant: 'destructive' });
      return;
    }
    try {
      // Add all resolved tracks sequentially (preserves order)
      for (const t of resolvedTracks) {
        await addTrackToPlaylist(playlistId, {
          trackId: t.trackId,
          title: t.title,
          artist: t.artist,
          imageUrl: t.imageUrl,
          audioUrl: t.audioUrl,
          youtubeId: t.youtubeId,
          duration: t.duration,
          addedBy: user?.uid,
        });
      }
      setAddedTo(prev => new Set([...prev, playlistId]));
      setJustAdded(prev => ({ ...prev, [playlistId]: resolvedTracks.map(t => t.imageUrl).filter((u): u is string => !!u) }));
      const label = isRelease
        ? `${resolvedTracks.length} track${resolvedTracks.length !== 1 ? 's' : ''} from "${displayTitle}"`
        : `"${displayTitle}"`;
      toast({ title: 'Added to playlist', description: `${label} added successfully.` });
    } catch (err) {
      console.error('Error adding to playlist:', err);
      toast({ title: 'Error', description: 'Failed to add to playlist.', variant: 'destructive' });
    }
  };

  const handleCreateAndAdd = async () => {
    if (!user?.uid || !newTitle.trim() || resolvedTracks.length === 0) return;
    setCreating(true);
    try {
      const newId = await createPlaylist({
        title: newTitle.trim(),
        creatorId: user.uid,
        creatorName: userProfile?.displayName || 'User',
        creatorAvatar: userProfile?.profileImageUrl,
        visibility: 'private',
        mediaFilter: 'all',
      });
      for (const t of resolvedTracks) {
        await addTrackToPlaylist(newId, {
          trackId: t.trackId,
          title: t.title,
          artist: t.artist,
          imageUrl: t.imageUrl,
          audioUrl: t.audioUrl,
          youtubeId: t.youtubeId,
          duration: t.duration,
          addedBy: user?.uid,
        });
      }
      setAddedTo(prev => new Set([...prev, newId]));
      const label = isRelease
        ? `${resolvedTracks.length} tracks from "${displayTitle}"`
        : `"${displayTitle}"`;
      toast({ title: 'Created & added', description: `Playlist "${newTitle.trim()}" created with ${label}.` });
      setNewTitle('');
      setShowNewForm(false);
      loadPlaylists();
    } catch (err) {
      console.error('Error creating playlist:', err);
      toast({ title: 'Error', description: 'Failed to create playlist.', variant: 'destructive' });
    } finally {
      setCreating(false);
    }
  };

  if (!user || resolvedTracks.length === 0) return null;

  const trigger = variant === 'icon' ? (
    <Button variant="ghost" size="icon" className={cn("h-8 w-8", className)} title="Add to playlist">
      <ListPlus className="h-4 w-4" />
    </Button>
  ) : variant === 'button' ? (
    <Button variant="outline" size="sm" className={className}>
      <ListPlus className="h-4 w-4 mr-1" /> Add to Playlist
    </Button>
  ) : (
    <button className={`flex items-center gap-2 w-full px-2 py-1.5 text-sm hover:bg-secondary rounded ${className}`}>
      <ListPlus className="h-4 w-4" /> Add to Playlist
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild onClick={e => e.stopPropagation()}>
        {trigger}
      </DialogTrigger>
      <DialogContent className="max-w-sm z-[200]">
        <DialogHeader>
          <DialogTitle>Add to Playlist</DialogTitle>
          <DialogDescription>
            {isRelease
              ? `Add ${resolvedTracks.length} track${resolvedTracks.length !== 1 ? 's' : ''} from "${displayTitle}" to a playlist`
              : `Add "${displayTitle}" to a playlist`
            }
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="space-y-1 max-h-[300px] overflow-y-auto">
            {playlists.length === 0 && !showNewForm && (
              <div className="text-center py-6 text-muted-foreground">
                <ListMusic className="h-8 w-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm mb-3">No playlists yet</p>
              </div>
            )}
            {playlists.map(pl => (
              <button
                key={pl.id}
                className="flex items-center gap-3 w-full p-2.5 rounded-md hover:bg-secondary/50 transition-colors text-left"
                onClick={() => handleAddToPlaylist(pl.id)}
                disabled={addedTo.has(pl.id)}
              >
                {(() => {
                  // The playlist's artwork as a slideshow; what was just added comes first.
                  const imgs = [...new Set([...(justAdded[pl.id] || []), ...(pl.coverImages || []), pl.coverImageUrl || ''].filter(Boolean))];
                  return (
                    <div className="relative w-10 h-10 overflow-hidden rounded bg-muted flex items-center justify-center flex-shrink-0">
                      {imgs.length > 0 ? <SlideshowImage images={imgs} alt="" sizes="40px" unoptimized /> : !addedTo.has(pl.id) && <ListMusic className="h-4 w-4 text-muted-foreground" />}
                      {addedTo.has(pl.id) && (
                        <span className="absolute inset-0 flex items-center justify-center bg-black/40">
                          <Check className="h-4 w-4 text-white" />
                        </span>
                      )}
                    </div>
                  );
                })()}
                <div className="min-w-0 flex-grow">
                  <p className="text-sm font-medium truncate">{pl.title}</p>
                  <p className="text-xs text-muted-foreground">{pl.trackCount} tracks</p>
                </div>
                {!addedTo.has(pl.id) && <Plus className="h-4 w-4 text-muted-foreground flex-shrink-0" />}
              </button>
            ))}
          </div>
        )}

        {showNewForm ? (
          <div className="flex gap-2 mt-2">
            <Input
              placeholder="New playlist name"
              value={newTitle}
              onChange={e => setNewTitle(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleCreateAndAdd()}
              autoFocus
            />
            <Button onClick={handleCreateAndAdd} disabled={!newTitle.trim() || creating} size="sm">
              {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Create'}
            </Button>
          </div>
        ) : (
          <Button variant="outline" className="w-full mt-2" onClick={() => setShowNewForm(true)}>
            <Plus className="h-4 w-4 mr-1" /> New Playlist
          </Button>
        )}
      </DialogContent>
    </Dialog>
  );
}
